import { localSaveFailed, localSaveSucceeded, requireWriteSuccess, serializeLocalWrite, localSavingIsSuspended } from '../services/localSaveState'
import { defineStore } from 'pinia'
import { ref, shallowRef, computed, watch, type Ref } from 'vue'
import { localHLC, isPlausibleHlc } from '../services/hlc'
import { syncEngine } from '../services/syncEngine'
import { findMatchRanges, searchTextBlocks, type NodeSearchResult } from '../services/corpusSearch'
import {
  createTermsMatcher,
  isBodySearchable,
  parseSearchQuery,
  queryNeedsBody,
  type SearchTerm,
} from '../services/searchQuery'
import { normalizeDocumentMetadata, normalizeDueDate, type DocumentMetadata } from '../services/documentMetadata'
import type { FrontmatterValues, PublishingProfile } from '../services/blogPublishing'
import { normalizeStatusId, normalizeSynopsis } from '../services/pageStatus'
import { useSettingsStore } from './settings'
import { createSyncedRows, type RemoteSettings } from '../services/syncedRows'
import { clonePageSetup, defaultPageSetup, normalizePageSetup, type PageSetup } from '../utils/pageSetup'

export type { DocumentMetadata }

const STORAGE_KEY_CORPUS_UI = 'ink-corpus-ui-state'
const MAX_RECENT_PAGES = 50

export interface CorpusNode {
  id: string
  name: string
  subtype: 'desk' | 'folder' | 'page'
  children?: CorpusNode[]
  expanded?: boolean
  position: number
  hlcTimestamp: number
  icon?: string | null
  color?: string | null
  /** Alternate names used by project lookup. Titles are always indexed separately. */
  aliases?: string[]
  /** Undefined is treated as true for manifests created before document metadata existed. */
  includeInExport?: boolean
  /** Undefined is treated as true for manifests created before document metadata existed. */
  includeInWordCount?: boolean
  /** Pages: base face inherited by text without an explicit font mark. */
  fontFamily?: string
  /** Pages: blog front matter values the writer has confirmed. */
  frontmatter?: FrontmatterValues
  /** Pages: where the post was last published, relative to the publish folder. */
  publishedFile?: string
  /** Desks and folders: static-site publishing profile for pages inside. */
  publishing?: PublishingProfile
  /** Pages: a line or two on what the page is for, shown on the overview. */
  synopsis?: string
  /** Pages: id of a status from Settings → Documents → Page Statuses. */
  status?: string
  /** Any node: the day it is due, 'YYYY-MM-DD'. */
  due?: string
  /** Pages: the page view's layout and the export default. Folders and desks: the last one applied inside. */
  pageSetup?: PageSetup
  isUnsaved?: boolean
}

export function getDocumentMetadata(node: CorpusNode): DocumentMetadata {
  return {
    aliases: Array.isArray(node.aliases) ? node.aliases : [],
    includeInExport: node.includeInExport !== false,
    includeInWordCount: node.includeInWordCount !== false,
    ...(node.fontFamily ? { fontFamily: node.fontFamily } : {}),
    ...(node.frontmatter ? { frontmatter: node.frontmatter } : {}),
    ...(node.publishedFile ? { publishedFile: node.publishedFile } : {}),
    ...(node.publishing ? { publishing: node.publishing } : {}),
    ...(node.synopsis ? { synopsis: node.synopsis } : {}),
    ...(node.status ? { status: node.status } : {}),
    ...(node.due ? { due: node.due } : {}),
    ...(node.pageSetup ? { pageSetup: node.pageSetup } : {}),
  }
}

/** Node fields carried by a synced metadata payload. */
function metadataNodeFields(raw: any): Partial<Pick<CorpusNode, 'aliases' | 'includeInExport' | 'includeInWordCount' | 'fontFamily' | 'frontmatter' | 'publishedFile' | 'publishing' | 'synopsis' | 'status' | 'due' | 'pageSetup'>> {
  const metadata = normalizeDocumentMetadata(raw)
  return {
    aliases: metadata.aliases,
    includeInExport: metadata.includeInExport,
    includeInWordCount: metadata.includeInWordCount,
    ...(metadata.fontFamily ? { fontFamily: metadata.fontFamily } : {}),
    frontmatter: metadata.frontmatter,
    publishedFile: metadata.publishedFile,
    publishing: metadata.publishing,
    synopsis: metadata.synopsis,
    status: metadata.status,
    due: metadata.due,
    pageSetup: metadata.pageSetup,
  }
}

export interface TrashEntry {
  node: CorpusNode
  originalParentId: string | null
  originalIndex: number
  deletedAt: number
}

/** Where a node sat in the tree, so a move can be put back exactly. */
export interface NodePlacement {
  id: string
  parentId: string | null
  index: number
  position: number
}

export type SearchView = 'list' | 'tree'

/** How the overview of a folder or desk lays out its contents. One choice for every folder. */
export type OverviewView = 'cards' | 'list' | 'outline'
export type OverviewSort = 'manual' | 'name' | 'words' | 'written' | 'status' | 'due'
/** Which lines of a page its overview card previews: the opening, or where the writing stops. */
export type OverviewPreview = 'start' | 'end'
const OVERVIEW_SORTS: OverviewSort[] = ['manual', 'name', 'words', 'written', 'status', 'due']

/**
 * Where a search (and a replace) looks. 'view' is the default: the desk being
 * viewed, or the whole corpus when the query asks for it (scope:all, desk:).
 */
export type SearchScope = { kind: 'view' } | { kind: 'corpus' } | { kind: 'node'; id: string }

export interface FlatSearchResult {
  node: CorpusNode
  /** Ancestor names below the current desk, outermost first. */
  path: string[]
  result: NodeSearchResult
}

function scoreSearchResult(
  node: CorpusNode,
  terms: SearchTerm[],
  result: Omit<NodeSearchResult, 'score'>,
): number {
  const name = node.name.toLocaleLowerCase()
  const aliases = (node.aliases || []).map(alias => alias.toLocaleLowerCase())
  let score = 0
  if (result.titleRanges.length) {
    for (const term of terms) {
      const text = term.text.toLocaleLowerCase()
      if (name === text) score += 1000
      else if (name.startsWith(text)) score += 700
      else if (name.includes(text)) score += 500
    }
    score = Math.max(score, 500)
  }
  if (result.aliasMatches.length) {
    score += terms.some(term => aliases.includes(term.text.toLocaleLowerCase())) ? 400 : 300
  }
  // Diminishing returns: 1 → 10, 10 → 40, 100 → 80.
  if (result.bodyCount) score += Math.round(Math.log2(result.bodyCount + 1) * 12)
  return score
}

function testText(matcher: RegExp, text: string): boolean {
  matcher.lastIndex = 0
  return matcher.test(text)
}

export const TRASH_RETENTION_DAYS = 30
const TRASH_RETENTION_MS = TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000

function generateId(subtype: 'desk' | 'folder' | 'page' = 'page'): string {
  return `${subtype}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
}

/**
 * Copy of a tree without lazily-created pages that were never edited. Those
 * drafts live only in memory; anything written to disk must leave them out,
 * or they come back on the next launch as permanent "Untitled" pages.
 */
function withoutUnsavedNodes(nodes: CorpusNode[]): CorpusNode[] {
  return nodes
    .filter((node) => !node.isUnsaved)
    .map((node) => node.children
      ? { ...node, children: withoutUnsavedNodes(node.children) }
      : node)
}

export function findNode(nodes: CorpusNode[], id: string): CorpusNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const found = findNode(node.children, id)
      if (found) return found
    }
  }
  return null
}

/** Returns the complete root-to-node path, including the requested node. */
export function findNodePath(nodes: CorpusNode[], id: string): CorpusNode[] {
  for (const node of nodes) {
    if (node.id === id) return [node]
    if (node.children?.length) {
      const childPath = findNodePath(node.children, id)
      if (childPath.length) return [node, ...childPath]
    }
  }
  return []
}

export function findParentList(
  nodes: CorpusNode[],
  id: string
): { list: CorpusNode[]; index: number; parent: CorpusNode | null } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return { list: nodes, index: i, parent: null }
    }
    if (nodes[i].children) {
      const res = findParentListInternal(nodes[i].children!, id, nodes[i])
      if (res) return res
    }
  }
  return null
}

function findParentListInternal(
  nodes: CorpusNode[],
  id: string,
  parent: CorpusNode
): { list: CorpusNode[]; index: number; parent: CorpusNode } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return { list: nodes, index: i, parent }
    }
    if (nodes[i].children) {
      const res = findParentListInternal(nodes[i].children!, id, nodes[i])
      if (res) return res
    }
  }
  return null
}

export function isDescendantNode(parent: CorpusNode, targetId: string): boolean {
  if (!parent.children || parent.children.length === 0) return false
  for (const child of parent.children) {
    if (child.id === targetId) return true
    if (isDescendantNode(child, targetId)) return true
  }
  return false
}

export function findFirstPage(nodes: CorpusNode[]): CorpusNode | null {
  for (const node of nodes) {
    if (node.subtype === 'page') return node
    if (node.children) {
      const found = findFirstPage(node.children)
      if (found) return found
    }
  }
  return null
}

/** `ids` sorted into the order they appear in the tree (depth first). */
export function nodeIdsInTreeOrder(nodes: CorpusNode[], ids: string[]): string[] {
  const wanted = new Set(ids)
  return flattenNodes(nodes).map(({ node }) => node.id).filter((id) => wanted.has(id))
}

function flattenNodes(nodes: CorpusNode[], parentId: string | null = null): Array<{ node: CorpusNode; parentId: string | null }> {
  const flattened: Array<{ node: CorpusNode; parentId: string | null }> = []
  for (const node of nodes) {
    flattened.push({ node, parentId })
    if (node.children) flattened.push(...flattenNodes(node.children, node.id))
  }
  return flattened
}

export function pruneDescendantNodeIds(nodes: CorpusNode[], ids: string[]): string[] {
  const idSet = new Set(ids)
  return ids.filter((id) => {
    const path = findNodePath(nodes, id)
    return !path.slice(0, -1).some((ancestor) => idSet.has(ancestor.id))
  })
}

export function getVisibleNodeIds(nodes: CorpusNode[], includeDeskChildren = false): string[] {
  const result: string[] = []
  for (const node of nodes) {
    result.push(node.id)
    if ((includeDeskChildren || node.subtype !== 'desk') && node.expanded && node.children?.length) {
      result.push(...getVisibleNodeIds(node.children, includeDeskChildren))
    }
  }
  return result
}

function trashEntriesFromRemote(nodes: any[]): TrashEntry[] {
  const deleted = nodes.filter((node) => node.deletedAt)
  const nodeMap = new Map<string, CorpusNode>()
  for (const item of deleted) {
    const subtype: CorpusNode['subtype'] = item.subtype || (item.type === 'folder' ? 'folder' : 'page')
    nodeMap.set(item.id, {
      id: item.id,
      name: item.name || 'Untitled',
      subtype,
      position: item.position || 0,
      hlcTimestamp: item.hlcTimestamp || 0,
      icon: item.icon ?? null,
      color: item.color ?? null,
      ...metadataNodeFields(item.metadata),
      children: [],
      ...(subtype !== 'desk' ? { expanded: true } : {}),
    })
  }

  const entries: TrashEntry[] = []
  for (const item of deleted) {
    const node = nodeMap.get(item.id)!
    if (item.parentId && nodeMap.has(item.parentId)) {
      nodeMap.get(item.parentId)!.children!.push(node)
    } else {
      entries.push({
        node,
        originalParentId: item.parentId ?? null,
        originalIndex: 0,
        deletedAt: Date.parse(item.deletedAt) || Date.now(),
      })
    }
  }
  return entries
}

function moveTrashEntryFiles(entry: TrashEntry, action: 'trashPage' | 'restorePage'): void {
  for (const { node } of flattenNodes([entry.node])) {
    if (node.subtype === 'page') {
      void (window as any).api?.[action]?.(node.id).catch((error: unknown) => {
        console.warn(`Failed to ${action === 'trashPage' ? 'trash' : 'restore'} remote page ${node.id}:`, error)
      })
    }
  }
}

/**
 * Clears node timestamps that came from a broken clock, so the cloud's copy
 * wins the next pull instead of every later change being refused locally.
 */
function repairNodeHlcs(nodes: CorpusNode[]): boolean {
  let repaired = false
  for (const { node } of flattenNodes(nodes)) {
    if (node.hlcTimestamp && !isPlausibleHlc(node.hlcTimestamp)) {
      node.hlcTimestamp = 0
      repaired = true
    }
  }
  return repaired
}

export const useCorpusStore = defineStore('corpus', () => {
  const tree: Ref<CorpusNode[]> = ref([])
  const trash: Ref<TrashEntry[]> = ref([])
  const activePageId: Ref<string | null> = ref(null)
  const selectedNodeId: Ref<string | null> = ref(null)
  const selectedNodeIds: Ref<string[]> = ref([])
  const isSelectionMode: Ref<boolean> = ref(false)
  const editingNodeId: Ref<string | null> = ref(null)
  const focusStack: Ref<string[]> = ref([])
  const pinned: Ref<boolean> = ref(false)
  /** Pages opened on this device, newest first, for the quick switcher. */
  const recentPageIds: Ref<string[]> = ref([])
  const searchQuery: Ref<string> = ref('')
  const searchView: Ref<SearchView> = ref('list')
  /** Page id → text blocks, filled by the Corpus Panel while a search is active. */
  const searchTextIndex = shallowRef<Map<string, string[]>>(new Map())
  /** Rows collapsed inside search results; the tree's own state is untouched. */
  const searchCollapsedIds = ref<Set<string>>(new Set())
  /** A node opened during a search, revealed in the tree once search ends. */
  let revealAfterSearchId: string | null = null
  const searchScope = ref<SearchScope>({ kind: 'view' })
  const replaceOpen = ref(false)
  const replaceText = ref('')
  const overviewFolderId: Ref<string | null> = ref(null)
  const overviewView: Ref<OverviewView> = ref('cards')
  const overviewSort: Ref<OverviewSort> = ref('manual')
  const overviewShowBalance: Ref<boolean> = ref(false)
  const overviewPreview: Ref<OverviewPreview> = ref('start')
  const overviewShowShape: Ref<boolean> = ref(false)
  /** The open page's notes are shown in place of its text (NotesOverview). */
  const notesViewOpen: Ref<boolean> = ref(false)

  function saveUiState() {
    try {
      const activeNode = activePageId.value ? findNode(tree.value, activePageId.value) : null
      const safeActivePageId = activeNode?.isUnsaved ? null : activePageId.value
      localStorage.setItem(
        STORAGE_KEY_CORPUS_UI,
        JSON.stringify({
          activePageId: safeActivePageId,
          focusStack: focusStack.value,
          pinned: pinned.value,
          searchView: searchView.value,
          overviewView: overviewView.value,
          overviewSort: overviewSort.value,
          overviewShowBalance: overviewShowBalance.value,
          overviewPreview: overviewPreview.value,
          overviewShowShape: overviewShowShape.value,
          recentPageIds: recentPageIds.value,
        })
      )
    } catch {}
  }

  function loadUiState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CORPUS_UI)
      if (!raw) return
      const data = JSON.parse(raw)
      activePageId.value = data.activePageId || null
      focusStack.value = Array.isArray(data.focusStack) ? data.focusStack : []
      pinned.value = !!data.pinned
      searchView.value = data.searchView === 'tree' ? 'tree' : 'list'
      overviewView.value = data.overviewView === 'list' || data.overviewView === 'outline' ? data.overviewView : 'cards'
      overviewSort.value = OVERVIEW_SORTS.includes(data.overviewSort) ? data.overviewSort : 'manual'
      overviewShowBalance.value = data.overviewShowBalance === true
      overviewPreview.value = data.overviewPreview === 'end' ? 'end' : 'start'
      overviewShowShape.value = data.overviewShowShape === true
      recentPageIds.value = Array.isArray(data.recentPageIds)
        ? data.recentPageIds.filter((id: unknown) => typeof id === 'string').slice(0, MAX_RECENT_PAGES)
        : []
    } catch {}
  }

  loadUiState()

  watch(activePageId, (id) => {
    if (!id) return
    recentPageIds.value = [id, ...recentPageIds.value.filter(other => other !== id)].slice(0, MAX_RECENT_PAGES)
  })

  watch([activePageId, recentPageIds, focusStack, pinned, searchView, overviewView, overviewSort, overviewShowBalance, overviewPreview, overviewShowShape], () => {
    saveUiState()
  }, { deep: true })

  // The page last opened on any device, so "At launch, open: Last page"
  // resumes where the writer left off elsewhere. activePageId above is only
  // this device's memory, and a browser that has none (or an older one) would
  // otherwise open the first page in the tree.
  const lastOpenedPage = createSyncedRows<string>({
    storageKey: 'ink-last-opened-page',
    remotePrefix: 'corpus.lastOpenedPage.',
    parse: (raw) => (typeof raw === 'string' && raw ? raw : null),
  })
  // Only the writer's own page opens are recorded: startup's restore must not
  // stamp this device's older page over a newer one from another device.
  let startupPageSettled = false
  // Until the writer opens a page or types, a newer page arriving from
  // another device replaces the one startup picked.
  let followSyncedPage = false
  // Set while the app, not the writer, changes the open page.
  let quietPageChange = false

  /** Opens a page without counting it as the writer's choice. */
  function setActivePageQuietly(id: string | null) {
    quietPageChange = true
    try { activePageId.value = id } finally { quietPageChange = false }
  }

  /** The synced last-opened page, when it is a saved page in the tree. */
  function syncedLastPageId(): string | null {
    const id = lastOpenedPage.values.value.current
    const node = id ? findNode(tree.value, id) : null
    return node?.subtype === 'page' && !node.isUnsaved ? node.id : null
  }

  /** Called once startup has opened its page. */
  function settleStartupPage(follow: boolean) {
    startupPageSettled = true
    followSyncedPage = follow
    // Startup's own content loading isn't the writer at work; a key press or
    // click is.
    if (follow && typeof window !== 'undefined') {
      const stop = () => {
        followSyncedPage = false
        window.removeEventListener('keydown', stop, true)
        window.removeEventListener('pointerdown', stop, true)
      }
      window.addEventListener('keydown', stop, true)
      window.addEventListener('pointerdown', stop, true)
    }
  }

  function recordOpenedPage() {
    const id = activePageId.value
    if (!startupPageSettled || quietPageChange || !id) return
    const node = findNode(tree.value, id)
    if (node && !node.isUnsaved && id !== lastOpenedPage.values.value.current) lastOpenedPage.write('current', id)
  }

  // Synchronous, so quietPageChange is still set when these run.
  watch(activePageId, () => {
    if (!startupPageSettled || quietPageChange) return
    followSyncedPage = false
    recordOpenedPage()
  }, { flush: 'sync' })

  // A new page becomes recordable on its first real edit.
  watch(() => (activePageId.value ? findNode(tree.value, activePageId.value)?.isUnsaved : undefined), (unsaved, wasUnsaved) => {
    if (wasUnsaved && unsaved === false) recordOpenedPage()
  }, { flush: 'sync' })

  /** Merges the synced last page from a settings pull; opens it if startup is still following. */
  async function syncLastOpenedPage(remote: RemoteSettings) {
    await lastOpenedPage.syncWithRemote(remote)
    if (!startupPageSettled || !followSyncedPage) return
    followSyncedPage = false
    const id = syncedLastPageId()
    if (!id || id === activePageId.value || overviewFolderId.value) return
    setActivePageQuietly(id)
  }

  /** The open page's notes, in corpus order; empty for a note-less page. */
  const activePageNotes = computed<CorpusNode[]>(() => {
    const page = activePageId.value ? findNode(tree.value, activePageId.value) : null
    if (page?.subtype !== 'page') return []
    return (page.children || []).filter((child) => child.subtype === 'page' && !child.isUnsaved)
  })

  /** A page's text is on screen, not a folder overview or the page's notes. */
  const isWritingPage = computed(() => Boolean(activePageId.value) && !overviewFolderId.value && !notesViewOpen.value)

  // Opening any page, or any folder, always shows text: the notes view never
  // carries over. Sync, so a navigation can reopen the notes right after.
  watch([activePageId, overviewFolderId], () => {
    notesViewOpen.value = false
  }, { flush: 'sync' })

  // The last note trashed or moved away leaves nothing to show.
  watch(() => activePageNotes.value.length, (count) => {
    if (!count) notesViewOpen.value = false
  })

  function toggleNotesView(open = !notesViewOpen.value) {
    notesViewOpen.value = open && !overviewFolderId.value && activePageNotes.value.length > 0
  }

  async function persistManifest(): Promise<boolean> {
    if (localSavingIsSuspended()) return false
    const activeNode = activePageId.value ? findNode(tree.value, activePageId.value) : null
    const snapshot = JSON.stringify({ version: 2, activePageId: activeNode?.isUnsaved ? null : activePageId.value, tree: withoutUnsavedNodes(tree.value), trash: trash.value })
    try {
      // Keep the latest complete tree across a failed native write or WebView suspension.
      localStorage.setItem('ink-manifest-pending-v1', snapshot)
      if (localStorage.getItem('ink-manifest-pending-v1') !== snapshot) throw new Error('Page list recovery write failed')
    } catch { /* Try primary storage even if the emergency journal is unavailable. */ }
    try {
      await serializeLocalWrite('manifest', async () => {
        requireWriteSuccess(await (window as any).api?.writeCorpusManifest?.(JSON.parse(snapshot)))
        try { if (localStorage.getItem('ink-manifest-pending-v1') === snapshot) localStorage.removeItem('ink-manifest-pending-v1') } catch { /* Primary storage has committed. */ }
      })
      localSaveSucceeded('manifest')
      return true
    } catch (cause) {
      localSaveFailed('manifest', cause, persistManifest)
      console.warn('Failed to persist corpus manifest:', cause)
      return false
    }
  }

  async function loadManifest() {
    try {
      if (typeof window !== 'undefined' && (window as any).api?.readCorpusManifest) {
        let recovery: any = null
        try {
          const raw = localStorage.getItem('ink-manifest-pending-v1')
          if (raw) {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed.tree)) recovery = parsed
          }
        } catch (cause) { console.warn('Manifest recovery journal unavailable:', cause) }
        const manifest = recovery ?? await (window as any).api.readCorpusManifest()
        if (manifest && Array.isArray(manifest.tree)) {
          // Older builds wrote never-edited drafts into the manifest, where
          // they piled up as empty "Untitled" pages. Drop any that leaked.
          const cleanTree = withoutUnsavedNodes(manifest.tree)
          const hadLeakedDrafts = flattenNodes(cleanTree).length !== flattenNodes(manifest.tree).length
          const loadedTrash: TrashEntry[] = Array.isArray(manifest.trash) ? manifest.trash : []
          const repairedClocks = repairNodeHlcs([...cleanTree, ...loadedTrash.map((entry) => entry.node).filter(Boolean)])
          tree.value = cleanTree
          trash.value = loadedTrash
          if (manifest.activePageId && findNode(tree.value, manifest.activePageId)) {
            activePageId.value = manifest.activePageId
          } else if (activePageId.value && !findNode(tree.value, activePageId.value)) {
            // Stale reference — e.g. an unsaved page from a previous session
            // that never got persisted. Clear it so startup recovery falls
            // back to the first real page instead of pointing at nothing.
            activePageId.value = null
          }
          if (hadLeakedDrafts || repairedClocks) void persistManifest()
        }
        void purgeExpiredTrash()
      }
    } catch (e) {
      console.warn('Failed to load corpus manifest:', e)
    }
  }

  // Active desk node (at the top of focus stack)
  const currentDesk = computed<CorpusNode | null>(() => {
    if (focusStack.value.length === 0) return null
    const topDeskId = focusStack.value[focusStack.value.length - 1]
    return findNode(tree.value, topDeskId)
  })

  // Header display name
  const currentDeskName = computed<string | null>(() => {
    return currentDesk.value ? currentDesk.value.name : null
  })

  // Visible tree based on focusStack
  const visibleTree = computed<CorpusNode[]>(() => {
    if (focusStack.value.length === 0) {
      return tree.value
    }
    // Traverse down focus stack
    let currentNodes: CorpusNode[] = tree.value
    let targetNode: CorpusNode | null = null
    for (const deskId of focusStack.value) {
      targetNode = findNode(currentNodes, deskId)
      if (targetNode && targetNode.children) {
        currentNodes = targetNode.children
      } else {
        const fallback = findNode(tree.value, deskId)
        if (fallback && fallback.children) {
          currentNodes = fallback.children
          targetNode = fallback
        }
      }
    }
    return targetNode && targetNode.children ? targetNode.children : currentNodes
  })

  const searchActive = computed(() => searchQuery.value.trim().length > 0)
  const parsedSearch = computed(() => parseSearchQuery(searchQuery.value))
  const searchNeedsBody = computed(() => searchActive.value && queryNeedsBody(parsedSearch.value))
  /** Highlights every search term in document text; also used to jump to a match. */
  const searchBodyMatcher = computed(() => parsedSearch.value.fields.has('body')
    ? createTermsMatcher(parsedSearch.value.terms.filter(isBodySearchable))
    : null)
  /** The page, folder or desk the search is limited to, if it still exists. */
  const searchScopeNode = computed<CorpusNode | null>(() => {
    const scope = searchScope.value
    return scope.kind === 'node' ? findNode(tree.value, scope.id) : null
  })
  const searchWholeCorpus = computed(() => !searchScopeNode.value
    && (searchScope.value.kind === 'corpus' || parsedSearch.value.wholeCorpus))
  const searchRootNodes = computed<CorpusNode[]>(() => {
    const node = searchScopeNode.value
    if (node) return node.subtype === 'page' ? [node] : (node.children || [])
    return searchWholeCorpus.value ? tree.value : visibleTree.value
  })

  // Nodes matching the parsed query, keyed by id.
  const searchResults = computed<Map<string, NodeSearchResult>>(() => {
    const results = new Map<string, NodeSearchResult>()
    const parsed = parsedSearch.value
    if (!searchActive.value || parsed.isEmpty) return results

    const { fields } = parsed
    const index = searchTextIndex.value
    const titleMatcher = createTermsMatcher(parsed.terms)
    const bodyMatcher = searchBodyMatcher.value
    const compile = (term: SearchTerm) => ({
      matcher: createTermsMatcher([term]),
      body: fields.has('body') && isBodySearchable(term),
    })
    const required = parsed.terms.map(compile)
    const excluded = parsed.excluded.map(compile)

    // 'hit' | 'miss' | 'pending' (the answer depends on text not loaded yet).
    function lookup(
      node: CorpusNode,
      blocks: string[] | undefined,
      term: { matcher: RegExp | null; body: boolean },
    ): 'hit' | 'miss' | 'pending' {
      const { matcher } = term
      if (!matcher) return 'miss'
      if (fields.has('title') && testText(matcher, node.name)) return 'hit'
      if (fields.has('alias') && (node.aliases || []).some(alias => testText(matcher, alias))) return 'hit'
      if (!term.body || node.subtype !== 'page') return 'miss'
      if (!blocks) return 'pending'
      return blocks.some(block => testText(matcher, block)) ? 'hit' : 'miss'
    }

    function matches(node: CorpusNode, ancestors: CorpusNode[], blocks: string[] | undefined): boolean {
      for (const term of required) {
        if (lookup(node, blocks, term) !== 'hit') return false
      }
      for (const term of excluded) {
        if (lookup(node, blocks, term) !== 'miss') return false
      }
      for (const filter of parsed.filters) {
        if (filter.needsBody && node.subtype === 'page' && !blocks) return false
        if (!filter.test({ node, ancestors, blocks, activePageId: activePageId.value })) return false
      }
      return true
    }

    // A page scope covers that page alone, not the notes beneath it.
    const scopeNode = searchScopeNode.value
    const pageOnly = scopeNode?.subtype === 'page'

    function visit(nodes: CorpusNode[], ancestors: CorpusNode[]) {
      for (const node of nodes) {
        const blocks = node.subtype === 'page' ? index.get(node.id) : undefined
        if (matches(node, ancestors, blocks)) {
          const titleRanges = fields.has('title') && titleMatcher ? findMatchRanges(node.name, titleMatcher) : []
          const aliasMatches = fields.has('alias') && titleMatcher
            ? (node.aliases || [])
              .map(alias => ({ alias, ranges: findMatchRanges(alias, titleMatcher) }))
              .filter(match => match.ranges.length)
            : []
          const body = bodyMatcher && blocks ? searchTextBlocks(blocks, bodyMatcher) : { count: 0, snippets: [] }
          const partial = { titleRanges, aliasMatches, bodyCount: body.count, snippets: body.snippets }
          results.set(node.id, { ...partial, score: scoreSearchResult(node, parsed.terms, partial) })
        }
        if (!pageOnly && node.children?.length) visit(node.children, [...ancestors, node])
      }
    }

    // Ancestors above the search root still count for desk:/under: filters.
    let root: CorpusNode[]
    if (scopeNode) {
      root = findNodePath(tree.value, scopeNode.id)
      if (pageOnly) root = root.slice(0, -1)
    } else {
      root = searchWholeCorpus.value ? [] : findNodePath(tree.value, currentDesk.value?.id ?? '')
    }
    visit(searchRootNodes.value, root)
    return results
  })

  // Filtered tree for search query
  const filteredTree = computed<CorpusNode[]>(() => {
    if (!searchActive.value) return visibleTree.value
    const results = searchResults.value
    const collapsed = searchCollapsedIds.value

    function filterNodes(nodes: CorpusNode[]): CorpusNode[] {
      const result: CorpusNode[] = []
      for (const node of nodes) {
        const matchesSelf = results.has(node.id)
        let filteredChildren: CorpusNode[] = []
        if (node.children) {
          filteredChildren = filterNodes(node.children)
        }
        if (matchesSelf || filteredChildren.length > 0) {
          result.push({
            ...node,
            // Matched branches start expanded. Collapsing one is remembered
            // only for this search, so the real tree state is never changed.
            expanded: !collapsed.has(node.id),
            children: node.children ? filteredChildren : undefined,
          })
        }
      }
      return result
    }

    return filterNodes(searchRootNodes.value)
  })

  // Matches as a single list, best first, for the list search view.
  const flatSearchResults = computed<FlatSearchResult[]>(() => {
    const results = searchResults.value
    const flat: FlatSearchResult[] = []
    if (!results.size) return flat

    function visit(nodes: CorpusNode[], path: string[]) {
      for (const node of nodes) {
        const result = results.get(node.id)
        if (result) flat.push({ node, path, result })
        if (node.children?.length) visit(node.children, [...path, node.name])
      }
    }

    visit(searchRootNodes.value, [])
    // Stable sort: equally ranked results keep manuscript order.
    return flat.sort((a, b) => b.result.score - a.result.score)
  })

  // Row order used by range selection and select-all.
  const navigableNodeIds = computed<string[]>(() => {
    if (!searchActive.value) return getVisibleNodeIds(filteredTree.value)
    if (searchView.value === 'list') return flatSearchResults.value.map(item => item.node.id)
    return getVisibleNodeIds(filteredTree.value, true)
  })

  // A chosen scope lasts while Replace is open or a search is running.
  watch([searchActive, replaceOpen], ([active, open]) => {
    if (!active && !open) searchScope.value = { kind: 'view' }
  })

  watch(searchActive, (active) => {
    if (active) return
    searchCollapsedIds.value = new Set()
    const pendingReveal = revealAfterSearchId
    revealAfterSearchId = null
    if (pendingReveal && findNode(tree.value, pendingReveal)) revealNode(pendingReveal)
  })

  function selectNode(id: string, mode: 'single' | 'toggle' | 'range' = 'single') {
    if (mode === 'single') {
      selectedNodeIds.value = [id]
      selectedNodeId.value = id
    } else if (mode === 'toggle') {
      const idx = selectedNodeIds.value.indexOf(id)
      if (idx !== -1) {
        selectedNodeIds.value = selectedNodeIds.value.filter((item) => item !== id)
        selectedNodeId.value = selectedNodeIds.value[selectedNodeIds.value.length - 1] || null
      } else {
        selectedNodeIds.value = [...selectedNodeIds.value, id]
        selectedNodeId.value = id
      }
    } else if (mode === 'range') {
      const visibleNodes = navigableNodeIds.value
      const anchorId = selectedNodeId.value || activePageId.value || id
      const anchorIdx = visibleNodes.indexOf(anchorId)
      const targetIdx = visibleNodes.indexOf(id)
      if (anchorIdx !== -1 && targetIdx !== -1) {
        const start = Math.min(anchorIdx, targetIdx)
        const end = Math.max(anchorIdx, targetIdx)
        const rangeIds = visibleNodes.slice(start, end + 1)
        const set = new Set([...selectedNodeIds.value, ...rangeIds])
        selectedNodeIds.value = Array.from(set)
        selectedNodeId.value = id
      } else {
        selectedNodeIds.value = [id]
        selectedNodeId.value = id
      }
    }
  }

  function toggleNodeSelection(id: string) {
    selectNode(id, 'toggle')
  }

  function rangeSelectNode(id: string) {
    selectNode(id, 'range')
  }

  function selectAllNodes() {
    selectedNodeIds.value = navigableNodeIds.value
    if (selectedNodeIds.value.length > 0) {
      selectedNodeId.value = selectedNodeIds.value[selectedNodeIds.value.length - 1]
    }
  }

  function clearSelection() {
    selectedNodeIds.value = []
    selectedNodeId.value = null
  }

  // Tree manipulation methods

  function getCurrentContainerId(): string | null {
    const targetId = selectedNodeId.value || activePageId.value
    if (targetId) {
      const node = findNode(tree.value, targetId)
      if (node) {
        if (node.subtype === 'folder' || node.subtype === 'desk') {
          return node.id
        }
        // If it's a page, create beside it in whichever node currently contains it.
        const parentRes = findParentList(tree.value, targetId)
        if (parentRes?.parent) {
          return parentRes.parent.id
        }
      }
    }
    // If inside a desk via focusStack
    if (focusStack.value.length > 0 && currentDesk.value) {
      return currentDesk.value.id
    }
    return null
  }

  /**
   * The page setup a new page starts with: the page it was created under or
   * next to, else the nearest folder or desk one was applied to. Copied, not
   * linked, so later changes to the source leave the new page alone.
   */
  function inheritedPageSetup(node: CorpusNode, targetId: string | null): PageSetup | undefined {
    const target = targetId ? findNode(tree.value, targetId) : null
    if (target?.subtype === 'page' && target.pageSetup) return target.pageSetup
    const ancestors = findNodePath(tree.value, node.id).slice(0, -1).reverse()
    return ancestors.find(ancestor => ancestor.pageSetup)?.pageSetup
  }

  function addNode(
    parentId: string | null,
    subtype: 'desk' | 'folder' | 'page' = 'page',
    name: string = subtype === 'desk' ? 'New Desk' : subtype === 'folder' ? 'New Folder' : 'New Page',
    activatePage: boolean = true,
    options: { deferCommit?: boolean; placement?: 'inside' | 'after' } = {},
  ): CorpusNode {
    const id = generateId(subtype)
    const hlcTimestamp = localHLC.now()
    const position = Date.now()
    const settings = useSettingsStore()

    const newNode: CorpusNode = {
      id,
      name,
      subtype,
      position,
      hlcTimestamp,
      icon: null,
      color: null,
      aliases: [],
      includeInExport: true,
      includeInWordCount: true,
      ...(subtype === 'page' ? { fontFamily: settings.fontFamily } : {}),
      children: [],
      ...(subtype !== 'desk' ? { expanded: true } : {}),
    }

    if (!parentId) {
      // If we are currently inside a desk and parentId is null, add to current desk root
      if (focusStack.value.length > 0) {
        const currentDeskNode = currentDesk.value
        if (currentDeskNode) {
          currentDeskNode.children = currentDeskNode.children || []
          currentDeskNode.children.push(newNode)
        } else {
          tree.value.push(newNode)
        }
      } else {
        tree.value.push(newNode)
      }
    } else {
      const parent = findNode(tree.value, parentId)
      if (parent) {
        const canNestInside = parent.subtype === 'folder'
          || parent.subtype === 'desk'
          || (parent.subtype === 'page' && subtype === 'page' && options.placement !== 'after')
        if (!canNestInside) {
          const res = findParentList(tree.value, parentId)
          if (res) res.list.splice(res.index + 1, 0, newNode)
        } else {
          parent.children = parent.children || []
          parent.children.push(newNode)
          if (parent.subtype !== 'desk') {
            parent.expanded = true
          }
        }
      } else {
        tree.value.push(newNode)
      }
    }

    if (subtype === 'page') {
      const inherited = inheritedPageSetup(newNode, parentId)
      if (inherited) newNode.pageSetup = clonePageSetup(inherited)
      // Lazy materialization: a freshly created page stays purely local/in-memory
      // (no manifest write, no tree-sync push, no disk write) until the user's
      // first real edit calls markPageSaved(). This is what stops "Untitled"
      // pages from accumulating when the app opens a blank page on startup
      // and the user never types anything.
      newNode.isUnsaved = true
      if (activatePage) {
        activePageId.value = id
        selectedNodeId.value = id
      }
      return newNode
    } else {
      selectedNodeId.value = id
    }

    if (options.deferCommit) return newNode

    persistManifest()

    const actualParentId = findParentList(tree.value, id)?.parent?.id
      ?? (focusStack.value.length > 0 ? currentDesk.value?.id || null : null)

    // Sync tree change (folders/desks are unaffected by this — they aren't
    // auto-created, so they don't have the same accumulation problem)
    syncEngine.pushTreeChanges([
      {
          id,
          parentId: actualParentId,
                               title: name,
                               type: 'folder',
                               subtype,
                               position,
                               hlcTimestamp,
                               deleted: false,
      },
    ])

    return newNode
  }

  function commitImportedContainers(nodes: CorpusNode[]) {
    if (!nodes.length) return
    persistManifest()
    syncEngine.pushTreeChanges(nodes.map((node) => {
      const parent = findParentList(tree.value, node.id)?.parent
      return {
        id: node.id,
        parentId: parent?.id ?? null,
        title: node.name,
        type: 'folder',
        subtype: node.subtype,
        position: node.position,
        hlcTimestamp: node.hlcTimestamp,
        deleted: false,
        icon: node.icon,
        color: node.color,
        metadata: getDocumentMetadata(node),
      }
    }))
  }

  function discardImportedContainers(nodes: CorpusNode[]) {
    for (const node of [...nodes].reverse()) {
      const result = findParentList(tree.value, node.id)
      if (result) result.list.splice(result.index, 1)
    }
  }

  function markPageSaved(id: string) {
    const node = findNode(tree.value, id)
    if (!node || !node.isUnsaved) return

      node.isUnsaved = false
      node.hlcTimestamp = localHLC.now()

      const res = findParentList(tree.value, id)
      const parentId = res?.parent?.id ?? null

      persistManifest()

      syncEngine.newDocuments.add(id)
      syncEngine.pushTreeChanges([
        {
          id,
          parentId,
          title: node.name,
          type: 'document',
          subtype: 'page',
          position: node.position,
          hlcTimestamp: node.hlcTimestamp,
          deleted: false,
          icon: node.icon,
          color: node.color,
          metadata: getDocumentMetadata(node),
        },
      ])
  }

  function getNodeParentId(id: string): string | null {
    return findParentList(tree.value, id)?.parent?.id ?? null
  }

  /**
   * The tree endpoint applies each mutation as a whole-row LWW update. Keep
   * structural fields on every metadata-only mutation so renaming or styling
   * a nested node cannot accidentally re-parent it to the corpus root.
   */
  function pushCurrentNode(node: CorpusNode): void {
    if (node.isUnsaved) return

    persistManifest()
    syncEngine.pushTreeChanges([
      {
        id: node.id,
        parentId: getNodeParentId(node.id),
        title: node.name,
        type: node.subtype === 'page' ? 'document' : 'folder',
        subtype: node.subtype,
        position: node.position || 1000,
        hlcTimestamp: node.hlcTimestamp,
        deleted: false,
        icon: node.icon,
        color: node.color,
        metadata: getDocumentMetadata(node),
      },
    ])
  }

  /**
   * Sends a node's current row again with a fresh clock, so it wins over
   * whatever the cloud holds or lacks. Used by the sync hub to repair a page
   * or folder whose row the cloud lost or refused. Resolves once the row is
   * sent (or queued, if the server cannot be reached).
   */
  async function resendNode(id: string): Promise<boolean> {
    const node = findNode(tree.value, id)
    if (!node || node.isUnsaved) return false
    node.hlcTimestamp = localHLC.now()
    persistManifest()
    await syncEngine.pushTreeChanges([
      {
        id: node.id,
        parentId: getNodeParentId(node.id),
        title: node.name,
        type: node.subtype === 'page' ? 'document' : 'folder',
        subtype: node.subtype,
        position: node.position || 1000,
        hlcTimestamp: node.hlcTimestamp,
        deleted: false,
        icon: node.icon,
        color: node.color,
        metadata: getDocumentMetadata(node),
      },
    ])
    return true
  }

  function discardUnsavedNode(id: string) {
    const res = findParentList(tree.value, id)
    if (!res) return
      const node = res.list[res.index]
      if (!node.isUnsaved) return // safety: never silently discard a real page

        res.list.splice(res.index, 1)

        if (activePageId.value === id) {
          const nextPage = findFirstPage(tree.value)
          activePageId.value = nextPage ? nextPage.id : null
        }
        // Intentionally no persistManifest() or tree push: it was never
        // synced anywhere, so there's nothing to undo. Opening it may still
        // have journaled the editor's first state, which would otherwise wait
        // forever as an unsent page.
        void syncEngine.forgetLocalPage(id).catch(() => {})
  }

  // Auto-discard an unedited page the moment the user navigates away from it
  watch(activePageId, (newId, oldId) => {
    if (oldId && oldId !== newId) {
      const oldNode = findNode(tree.value, oldId)
      if (oldNode?.isUnsaved) {
        discardUnsavedNode(oldId)
      }
    }
  })

  function renameNode(id: string, newName: string) {
    const node = findNode(tree.value, id)
    if (node) {
      node.name = newName
      node.hlcTimestamp = localHLC.now()
      pushCurrentNode(node)
    }
  }

  async function trashNodes(ids: string[]): Promise<TrashEntry[]> {
    if (!ids || ids.length === 0) return []
    const topLevelIds = pruneDescendantNodeIds(tree.value, ids)
    if (topLevelIds.length === 0) return []

    // 1. Gather all top-level nodes to remove and collect all underlying pages
    const nodesToRemove: CorpusNode[] = []
    for (const id of topLevelIds) {
      const node = findNode(tree.value, id)
      if (node) nodesToRemove.push(node)
    }
    if (nodesToRemove.length === 0) return []

    const allPages = flattenNodes(nodesToRemove).filter(({ node }) => node.subtype === 'page')

    // 2. Move every native document in the subtrees before mutating UI state
    const movedPageIds: string[] = []
    try {
      for (const { node } of allPages) {
        const result = await (window as any).api?.trashPage?.(node.id)
        if (result && !result.success) {
          throw new Error(result.error || `Could not move "${node.name}" to Trash`)
        }
        movedPageIds.push(node.id)
      }
    } catch (error) {
      await Promise.all(movedPageIds.map((pageId) => (window as any).api?.restorePage?.(pageId)))
      throw error
    }

    // 3. Remove each node from its parent list and add to trash
    const entries: TrashEntry[] = []
    const syncChanges: any[] = []

    for (const id of topLevelIds) {
      const res = findParentList(tree.value, id)
      if (res) {
        const removed = res.list[res.index]
        res.list.splice(res.index, 1)

        const entry: TrashEntry = {
          node: removed,
          originalParentId: res.parent?.id ?? null,
          originalIndex: res.index,
          deletedAt: Date.now(),
        }
        trash.value.unshift(entry)
        entries.push(entry)

        const changes = flattenNodes([removed], entry.originalParentId).map(({ node, parentId }) => ({
          id: node.id,
          parentId,
          title: node.name,
          type: node.subtype === 'page' ? ('document' as const) : ('folder' as const),
          subtype: node.subtype,
          position: node.position || 1000,
          hlcTimestamp: localHLC.now(),
          deleted: true,
        }))
        syncChanges.push(...changes)
      }
    }

    // 4. If deleted nodes contain activePageId, pick next page
    const activeContained = nodesToRemove.some(
      (n) => n.id === activePageId.value || (n.children && findNode(n.children, activePageId.value || ''))
    )
    if (activeContained) {
      const nextPage = findFirstPage(tree.value)
      activePageId.value = nextPage ? nextPage.id : null
    }

    // 5. If deleted nodes are in focusStack, trim stack
    for (const n of nodesToRemove) {
      const stackIdx = focusStack.value.indexOf(n.id)
      if (stackIdx !== -1) {
        focusStack.value = focusStack.value.slice(0, stackIdx)
      }
    }

    // 6. If overviewFolderId was inside a deleted node, reset it
    const overviewContained = nodesToRemove.some(
      (n) => n.id === overviewFolderId.value || (n.children && findNode(n.children, overviewFolderId.value || ''))
    )
    if (overviewContained) {
      overviewFolderId.value = null
    }

    // 7. Clean up selection
    const deletedIdSet = new Set(flattenNodes(nodesToRemove).map(({ node }) => node.id))
    selectedNodeIds.value = selectedNodeIds.value.filter((id) => !deletedIdSet.has(id))
    if (selectedNodeId.value && deletedIdSet.has(selectedNodeId.value)) {
      selectedNodeId.value = selectedNodeIds.value[0] || null
    }
    if (editingNodeId.value && deletedIdSet.has(editingNodeId.value)) {
      editingNodeId.value = null
    }

    persistManifest()

    if (syncChanges.length > 0) {
      await syncEngine.pushTreeChanges(syncChanges)
    }

    return entries
  }

  async function trashNode(id: string): Promise<TrashEntry | null> {
    const entries = await trashNodes([id])
    return entries[0] || null
  }

  /** Backward-compatible alias. New UI should call trashNode explicitly. */
  const deleteNode = trashNode
  const deleteNodes = trashNodes

  async function restoreTrashEntry(id: string): Promise<CorpusNode | null> {
    const trashIndex = trash.value.findIndex((entry) => entry.node.id === id)
    if (trashIndex === -1) return null
    const entry = trash.value[trashIndex]
    const pages = flattenNodes([entry.node]).filter(({ node }) => node.subtype === 'page')

    const restoredPageIds: string[] = []
    try {
      for (const { node } of pages) {
        const result = await (window as any).api?.restorePage?.(node.id)
        if (result && !result.success) {
          throw new Error(result.error || `Could not restore "${node.name}"`)
        }
        restoredPageIds.push(node.id)
      }
    } catch (error) {
      await Promise.all(restoredPageIds.map((pageId) => (window as any).api?.trashPage?.(pageId)))
      throw error
    }

    trash.value.splice(trashIndex, 1)
    const parent = entry.originalParentId ? findNode(tree.value, entry.originalParentId) : null
    const destination = parent?.children ?? tree.value
    destination.splice(Math.min(entry.originalIndex, destination.length), 0, entry.node)

    const changes = flattenNodes([entry.node], parent?.id ?? null).map(({ node, parentId }) => ({
      id: node.id,
      parentId,
      title: node.name,
      type: node.subtype === 'page' ? 'document' as const : 'folder' as const,
      subtype: node.subtype,
      position: node.position || 1000,
      hlcTimestamp: localHLC.now(),
      deleted: false,
      icon: node.icon,
      color: node.color,
      metadata: getDocumentMetadata(node),
    }))
    await persistManifest()
    await syncEngine.pushTreeChanges(changes)
    return entry.node
  }

  async function deleteTrashEntryForever(id: string): Promise<boolean> {
    const trashIndex = trash.value.findIndex((entry) => entry.node.id === id)
    if (trashIndex === -1) return false
    const entry = trash.value[trashIndex]
    const nodes = flattenNodes([entry.node])

    for (const { node } of nodes) {
      if (node.subtype === 'page') {
        const result = await (window as any).api?.deletePage?.(node.id)
        if (result && !result.success) {
          throw new Error(result.error || `Could not permanently delete "${node.name}"`)
        }
        // Its live edits can never be sent once the page is gone.
        await syncEngine.forgetLocalPage(node.id).catch(() => {})
      } else {
        await syncEngine.enqueueMutation('document', 'delete', node.id, {})
      }
    }

    trash.value.splice(trashIndex, 1)
    await persistManifest()
    return true
  }

  /**
   * Forgets Trash items another device (or the server's retention job)
   * permanently deleted. Only Trash is touched: a live node with a purged id
   * was restored here before the purge arrived, and its pending push recreates
   * it in the cloud. Ids the same pull returns as rows were recreated and stay.
   */
  async function applyRemotePurges(purgedIds: string[], remoteNodes: any[]): Promise<void> {
    if (purgedIds.length === 0) return
    const recreated = new Set(remoteNodes.map((node) => node.id))
    const purged = new Set(purgedIds.filter((id) => !recreated.has(id)))
    if (purged.size === 0) return

    // A remote purge must not erase writing this device has not delivered.
    const unsentPages = new Set((await syncEngine.listPagesWithUnsentEdits()).map(page => page.docId))
    const removedPages: string[] = []
    let removedAny = false
    const nextTrash: TrashEntry[] = []
    // Returns the node without its purged descendants, or null if it was purged
    // itself. A surviving child of a purged node becomes its own trash entry
    // until its own purge arrives.
    const prune = (node: CorpusNode, entry: TrashEntry): CorpusNode | null => {
      if (purged.has(node.id)) {
        if (node.subtype === 'page' && unsentPages.has(node.id)) return node
        removedAny = true
        if (node.subtype === 'page') removedPages.push(node.id)
        for (const child of node.children ?? []) {
          const survivor = prune(child, entry)
          if (survivor) nextTrash.push({ ...entry, node: survivor, originalParentId: null, originalIndex: 0 })
        }
        return null
      }
      if (node.children?.length) {
        node.children = node.children
          .map((child) => prune(child, entry))
          .filter((child): child is CorpusNode => child !== null)
      }
      return node
    }
    for (const entry of trash.value) {
      const survivor = prune(entry.node, entry)
      if (survivor) nextTrash.push({ ...entry, node: survivor })
    }
    if (!removedAny) return

    trash.value = nextTrash
    for (const id of removedPages) {
      try {
        await (window as any).api?.deleteLocalPage?.(id)
      } catch (error) {
        console.warn(`Failed to remove local files for purged page ${id}:`, error)
      }
      await syncEngine.forgetLocalPage(id).catch(() => {})
    }
    await persistManifest()
  }

  async function purgeExpiredTrash(now = Date.now()): Promise<void> {
    const expiredIds = trash.value
      .filter((entry) => now - entry.deletedAt >= TRASH_RETENTION_MS)
      .map((entry) => entry.node.id)
    if (expiredIds.length === 0) return
    const unsentPages = new Set((await syncEngine.listPagesWithUnsentEdits()).map(page => page.docId))
    const containsUnsent = (node: CorpusNode): boolean => unsentPages.has(node.id) || (node.children ?? []).some(containsUnsent)
    for (const id of expiredIds) {
      const entry = trash.value.find(item => item.node.id === id)
      if (entry && containsUnsent(entry.node)) continue
      try {
        await deleteTrashEntryForever(id)
      } catch (error) {
        console.warn('Failed to purge expired trash entry:', error)
      }
    }
  }

  function moveNode(
    sourceId: string,
    targetId: string | null,
    position: 'inside' | 'before' | 'after' = 'inside'
  ) {
    if (sourceId === targetId) return

    if (targetId) {
      const sourceNode = findNode(tree.value, sourceId)
      if (sourceNode && isDescendantNode(sourceNode, targetId)) return
    }

    const sourceRes = findParentList(tree.value, sourceId)
    if (!sourceRes) return

    const [movedNode] = sourceRes.list.splice(sourceRes.index, 1)
    let newParentId: string | null = null
    let newPosition = Date.now()

    if (!targetId) {
      if (focusStack.value.length > 0 && currentDesk.value) {
        currentDesk.value.children = currentDesk.value.children || []
        currentDesk.value.children.push(movedNode)
        newParentId = currentDesk.value.id
      } else {
        tree.value.push(movedNode)
      }
    } else {
      const targetNode = findNode(tree.value, targetId)
      if (!targetNode) {
        tree.value.push(movedNode)
      } else if (position === 'inside' && (
        targetNode.subtype === 'folder'
        || targetNode.subtype === 'desk'
        || (targetNode.subtype === 'page' && !targetNode.isUnsaved && movedNode.subtype === 'page')
      )) {
        targetNode.children = targetNode.children || []
        targetNode.children.push(movedNode)
        if (targetNode.subtype !== 'desk') {
          targetNode.expanded = true
        }
        newParentId = targetId
        if (targetNode.children.length > 1) {
          const prev = targetNode.children[targetNode.children.length - 2]
          newPosition = (prev.position || 0) + 1000
        } else {
          newPosition = 1000
        }
      } else {
        const targetRes = findParentList(tree.value, targetId)
        if (targetRes) {
          const insertIdx = position === 'after' ? targetRes.index + 1 : targetRes.index
          targetRes.list.splice(insertIdx, 0, movedNode)
          const prevNode = insertIdx > 0 ? targetRes.list[insertIdx - 1] : null
          const nextNode = insertIdx < targetRes.list.length - 1 ? targetRes.list[insertIdx + 1] : null
          if (!prevNode && !nextNode) {
            newPosition = 1000
          } else if (!prevNode && nextNode) {
            newPosition = (nextNode.position || 1000) - 1000
          } else if (prevNode && !nextNode) {
            newPosition = (prevNode.position || 0) + 1000
          } else if (prevNode && nextNode) {
            newPosition = ((prevNode.position || 0) + (nextNode.position || 0)) / 2
          }
          newParentId = targetRes.parent ? targetRes.parent.id : (focusStack.value.length > 0 ? currentDesk.value?.id || null : null)
        } else {
          tree.value.push(movedNode)
        }
      }
    }

    movedNode.position = newPosition
    movedNode.hlcTimestamp = localHLC.now()
    persistManifest()

    syncEngine.pushTreeChanges([
      {
        id: sourceId,
        parentId: newParentId,
        title: movedNode.name,
        type: movedNode.subtype === 'page' ? 'document' : 'folder',
        subtype: movedNode.subtype,
        position: newPosition,
        hlcTimestamp: movedNode.hlcTimestamp,
        deleted: false,
        icon: movedNode.icon,
        color: movedNode.color,
        metadata: getDocumentMetadata(movedNode),
      },
    ])
  }

  function getHeaderDropInfo(sourceId: string | null): { canDrop: boolean; targetName: string; targetParentId: string | null } | null {
    if (!sourceId) return null
    const sourceNode = findNode(tree.value, sourceId)
    if (!sourceNode) return null

    // If inside a desk via focusStack
    if (focusStack.value.length > 0 && currentDesk.value) {
      if (sourceId === currentDesk.value.id) return null

      const deskParentRes = findParentList(tree.value, currentDesk.value.id)
      const parentNode = deskParentRes ? deskParentRes.parent : null

      if (parentNode) {
        return {
          canDrop: true,
          targetName: parentNode.name,
          targetParentId: parentNode.id,
        }
      } else {
        return {
          canDrop: true,
          targetName: 'Root',
          targetParentId: null,
        }
      }
    }

    // If at true root level
    if (focusStack.value.length === 0) {
      const sourceRes = findParentList(tree.value, sourceId)
      if (!sourceRes || sourceRes.parent === null) {
        return null
      }
      return {
        canDrop: true,
        targetName: 'Root',
        targetParentId: null,
      }
    }

    return null
  }

  function moveNodeToHeaderParent(sourceId: string) {
    const dropInfo = getHeaderDropInfo(sourceId)
    if (!dropInfo || !dropInfo.canDrop) return

    const sourceRes = findParentList(tree.value, sourceId)
    if (!sourceRes) return

    const [movedNode] = sourceRes.list.splice(sourceRes.index, 1)
    let newParentId: string | null = null
    let newPosition = Date.now()

    if (dropInfo.targetParentId) {
      const targetParent = findNode(tree.value, dropInfo.targetParentId)
      if (!targetParent) {
        tree.value.push(movedNode)
      } else {
        targetParent.children = targetParent.children || []
        let insertIdx = targetParent.children.length
        if (currentDesk.value) {
          const idx = targetParent.children.findIndex(n => n.id === currentDesk.value?.id)
          if (idx !== -1) insertIdx = idx + 1
        }
        targetParent.children.splice(insertIdx, 0, movedNode)
        newParentId = targetParent.id

        const prevNode = insertIdx > 0 ? targetParent.children[insertIdx - 1] : null
        const nextNode = insertIdx < targetParent.children.length - 1 ? targetParent.children[insertIdx + 1] : null
        if (!prevNode && !nextNode) {
          newPosition = 1000
        } else if (!prevNode && nextNode) {
          newPosition = (nextNode.position || 1000) - 1000
        } else if (prevNode && !nextNode) {
          newPosition = (prevNode.position || 0) + 1000
        } else if (prevNode && nextNode) {
          newPosition = ((prevNode.position || 0) + (nextNode.position || 0)) / 2
        }
      }
    } else {
      // Moving to Root (tree.value)
      let insertIdx = tree.value.length
      if (currentDesk.value) {
        const idx = tree.value.findIndex(n => n.id === currentDesk.value?.id)
        if (idx !== -1) insertIdx = idx + 1
      }
      tree.value.splice(insertIdx, 0, movedNode)
      newParentId = null

      const prevNode = insertIdx > 0 ? tree.value[insertIdx - 1] : null
      const nextNode = insertIdx < tree.value.length - 1 ? tree.value[insertIdx + 1] : null
      if (!prevNode && !nextNode) {
        newPosition = 1000
      } else if (!prevNode && nextNode) {
        newPosition = (nextNode.position || 1000) - 1000
      } else if (prevNode && !nextNode) {
        newPosition = (prevNode.position || 0) + 1000
      } else if (prevNode && nextNode) {
        newPosition = ((prevNode.position || 0) + (nextNode.position || 0)) / 2
      }
    }

    movedNode.position = newPosition
    movedNode.hlcTimestamp = localHLC.now()
    persistManifest()

    syncEngine.pushTreeChanges([
      {
        id: sourceId,
        parentId: newParentId,
        title: movedNode.name,
        type: movedNode.subtype === 'page' ? 'document' : 'folder',
        subtype: movedNode.subtype,
        position: newPosition,
        hlcTimestamp: movedNode.hlcTimestamp,
        deleted: false,
        icon: movedNode.icon,
        color: movedNode.color,
        metadata: getDocumentMetadata(movedNode),
      },
    ])
  }

  function captureNodePlacements(ids: string[]): NodePlacement[] {
    const placements: NodePlacement[] = []
    for (const id of ids) {
      const res = findParentList(tree.value, id)
      if (res) placements.push({ id, parentId: res.parent?.id ?? null, index: res.index, position: res.list[res.index].position })
    }
    return placements
  }

  /** Puts nodes back where captureNodePlacements found them. */
  function restoreNodePlacements(placements: NodePlacement[]) {
    // Lift every node out first, then reinsert by ascending original index,
    // so nodes returning to the same list land in their original order.
    const lifted: Array<{ node: CorpusNode; placement: NodePlacement; parent: CorpusNode | null }> = []
    for (const placement of placements) {
      const res = findParentList(tree.value, placement.id)
      if (!res) continue // trashed or deleted since
      const node = res.list[res.index]
      const parent = placement.parentId ? findNode(tree.value, placement.parentId) : null
      if (placement.parentId && (!parent || isDescendantNode(node, parent.id))) continue
      res.list.splice(res.index, 1)
      lifted.push({ node, placement, parent })
    }
    if (lifted.length === 0) return

    const changes: any[] = []
    for (const { node, placement, parent } of lifted.sort((a, b) => a.placement.index - b.placement.index)) {
      const destination = parent ? (parent.children ??= []) : tree.value
      destination.splice(Math.min(placement.index, destination.length), 0, node)
      node.position = placement.position
      node.hlcTimestamp = localHLC.now()
      changes.push({
        id: node.id,
        parentId: parent?.id ?? null,
        title: node.name,
        type: node.subtype === 'page' ? 'document' : 'folder',
        subtype: node.subtype,
        position: node.position || 1000,
        hlcTimestamp: node.hlcTimestamp,
        deleted: false,
        icon: node.icon,
        color: node.color,
        metadata: getDocumentMetadata(node),
      })
    }
    persistManifest()
    syncEngine.pushTreeChanges(changes)
  }

  function toggleNode(nodeId: string) {
    if (searchActive.value) {
      const collapsed = new Set(searchCollapsedIds.value)
      if (!collapsed.delete(nodeId)) collapsed.add(nodeId)
      searchCollapsedIds.value = collapsed
      return
    }
    const node = findNode(tree.value, nodeId)
    if (node && node.subtype !== 'desk' && (node.children?.length || 0) > 0) {
      node.expanded = !node.expanded
      persistManifest()
    }
  }

  function collapseAll() {
    const collapse = (nodes: CorpusNode[]) => {
      for (const node of nodes) {
        if (node.subtype !== 'desk') node.expanded = false
        if (node.children) collapse(node.children)
      }
    }
    collapse(visibleTree.value)
    persistManifest()
  }

  /**
   * Makes a node locatable in the current panel without forcing the panel
   * itself open. Ancestors are expanded and the containing Desk is entered.
   */
  function revealNode(nodeId: string): CorpusNode[] {
    const path = findNodePath(tree.value, nodeId)
    if (!path.length) return []

    if (searchActive.value) {
      if (findNode(filteredTree.value, nodeId)) {
        // Keep the search and the tree as they are; reveal when search ends.
        selectedNodeId.value = nodeId
        revealAfterSearchId = nodeId
        return path
      }
      // An unrelated stale query would otherwise make the revealed row invisible.
      revealAfterSearchId = null
      searchQuery.value = ''
    }

    const ancestors = path.slice(0, -1)
    const containingDesks = ancestors
      .filter(node => node.subtype === 'desk')
      .map(node => node.id)
    if (
      containingDesks.length !== focusStack.value.length ||
      containingDesks.some((id, index) => focusStack.value[index] !== id)
    ) {
      focusStack.value = containingDesks
    }

    let expansionChanged = false
    for (const ancestor of ancestors) {
      if (ancestor.subtype !== 'desk' && ancestor.children?.length && !ancestor.expanded) {
        ancestor.expanded = true
        expansionChanged = true
      }
    }
    selectedNodeId.value = nodeId
    if (expansionChanged) void persistManifest()
    return path
  }

  /** Kept for older callers while page rows gain the same disclosure behavior. */
  const toggleFolder = toggleNode

  function setNodeSubtype(id: string, subtype: 'desk' | 'folder') {
    const node = findNode(tree.value, id)
    if (node && (node.subtype === 'desk' || node.subtype === 'folder')) {
      node.subtype = subtype
      if (subtype === 'folder') {
        node.expanded = true
      }
      node.hlcTimestamp = localHLC.now()
      pushCurrentNode(node)
    }
  }

  function setNodeIcon(id: string, icon: string | null) {
    const node = findNode(tree.value, id)
    if (node) {
      node.icon = icon
      node.hlcTimestamp = localHLC.now()
      pushCurrentNode(node)
    }
  }

  function setNodeColor(id: string, color: string | null) {
    const node = findNode(tree.value, id)
    if (node) {
      node.color = color
      node.hlcTimestamp = localHLC.now()
      pushCurrentNode(node)
    }
  }

  function pushMetadataChange(node: CorpusNode) {
    node.hlcTimestamp = localHLC.now()
    pushCurrentNode(node)
  }

  /** Stores (or with null, clears) a desk or folder's blog publishing profile. */
  function setPublishingProfile(id: string, profile: PublishingProfile | null) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype === 'page') return
    node.publishing = profile ?? undefined
    pushMetadataChange(node)
  }

  function setPageFrontmatter(id: string, values: FrontmatterValues) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return
    node.frontmatter = values
    // An unsaved page is not in the cloud tree yet; markPageSaved carries it.
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  function setPublishedFile(id: string, path: string | null) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return
    node.publishedFile = path ?? undefined
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  function setPageSynopsis(id: string, synopsis: string | null) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return
    const next = normalizeSynopsis(synopsis ?? '')
    if ((node.synopsis ?? undefined) === next) return
    node.synopsis = next
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  /** Sets (or with null, clears) the status of each page in `ids`. */
  function setPageStatus(ids: string[], status: string | null) {
    const next = normalizeStatusId(status)
    for (const id of ids) {
      const node = findNode(tree.value, id)
      if (!node || node.subtype !== 'page' || (node.status ?? undefined) === next) continue
      node.status = next
      if (!node.isUnsaved) pushMetadataChange(node)
    }
  }

  /** Sets (or with null, clears) the day a page, folder, or desk is due. */
  function setNodeDue(id: string, due: string | null) {
    const node = findNode(tree.value, id)
    if (!node) return
    const next = normalizeDueDate(due)
    if ((node.due ?? undefined) === next) return
    node.due = next
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  /** Changes the page-wide face inherited by unformatted text and neutral empty lines. */
  function setPageFontFamily(id: string, fontFamily: string) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return
    const next = fontFamily.trim().slice(0, 160)
    if (!next || node.fontFamily === next) return
    node.fontFamily = next
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  /** The page setup a page is laid out with: its own, or the default in its typeface. */
  function pageSetupFor(id: string): PageSetup {
    const node = findNode(tree.value, id)
    return node?.pageSetup ?? defaultPageSetup(node?.fontFamily || useSettingsStore().fontFamily)
  }

  /** Sets (or with null, clears back to the default) a page's page setup. */
  function setPageSetup(id: string, setup: PageSetup | null) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return
    const next = setup ? normalizePageSetup(setup) : undefined
    if (JSON.stringify(node.pageSetup) === JSON.stringify(next)) return
    node.pageSetup = next
    if (node.isUnsaved) return
    pushMetadataChange(node)
  }

  /**
   * Gives a folder or desk and every page inside it (notes included) a copy
   * of `setup`. The folder keeps it so pages created there later start with it.
   */
  function applyPageSetupToFolder(id: string, setup: PageSetup) {
    const folder = findNode(tree.value, id)
    if (!folder || folder.subtype === 'page') return
    const normalized = normalizePageSetup(setup)
    if (!normalized) return
    const serialized = JSON.stringify(normalized)
    const changed: CorpusNode[] = []
    const visit = (node: CorpusNode) => {
      if (JSON.stringify(node.pageSetup) !== serialized) {
        node.pageSetup = clonePageSetup(normalized)
        node.hlcTimestamp = localHLC.now()
        if (!node.isUnsaved) changed.push(node)
      }
      node.children?.forEach(visit)
    }
    visit(folder)
    if (!changed.length) return
    persistManifest()
    syncEngine.pushTreeChanges(changed.map(node => ({
      id: node.id,
      parentId: getNodeParentId(node.id),
      title: node.name,
      type: node.subtype === 'page' ? 'document' : 'folder',
      subtype: node.subtype,
      position: node.position || 1000,
      hlcTimestamp: node.hlcTimestamp,
      deleted: false,
      icon: node.icon,
      color: node.color,
      metadata: getDocumentMetadata(node),
    })))
  }

  function setDocumentMetadata(id: string, metadata: DocumentMetadata) {
    const node = findNode(tree.value, id)
    if (!node || node.subtype !== 'page') return

    const seen = new Set<string>()
    node.aliases = metadata.aliases
      .map((alias) => alias.trim().slice(0, 160))
      .filter((alias) => {
        const key = alias.toLocaleLowerCase()
        if (!alias || seen.has(key)) return false
        seen.add(key)
        return true
      })
      .slice(0, 100)
    node.includeInExport = metadata.includeInExport
    node.includeInWordCount = metadata.includeInWordCount
    node.hlcTimestamp = localHLC.now()
    pushCurrentNode(node)
  }

  function enterDesk(deskId: string) {
    const path = findNodePath(tree.value, deskId)
    const desk = path[path.length - 1]
    if (desk && desk.subtype === 'desk') {
      focusStack.value = path
        .filter(node => node.subtype === 'desk')
        .map(node => node.id)
      selectedNodeId.value = deskId
    }
  }

  function exitDesk() {
    if (focusStack.value.length > 0) {
      focusStack.value.pop()
    }
  }

  // Opens the overview for a folder or desk. Clicking the same
  // folder/desk again closes it, so the row acts as a toggle.
  function openFolderOverview(id: string) {
    const node = findNode(tree.value, id)
    if (!node || (node.subtype !== 'folder' && node.subtype !== 'desk')) return
    overviewFolderId.value = overviewFolderId.value === id ? null : id
  }

  function closeFolderOverview() {
    overviewFolderId.value = null
  }

  /**
   * Reconstructs or updates nested tree from remote node array.
   * If isDelta is true (or attached on remoteNodes) and local tree is non-empty,
   * performs incremental reconciliation (LWW updates, insertions, and tombstone deletions).
   */
  function applyRemoteTree(remoteNodes: any[], isDeltaOpt?: boolean) {
    if (!Array.isArray(remoteNodes)) return
    const isDelta = isDeltaOpt !== undefined ? isDeltaOpt : Boolean((remoteNodes as any).isDelta)

    if (isDelta && tree.value.length > 0) {
      // ── Delta Reconciliation ──────────────────────────────────────────────
      // 1. Flatten current tree into a map of { node, parentId }
      const existingMap = new Map<string, { node: CorpusNode; parentId: string | null }>()
      const flattenTree = (nodes: CorpusNode[], parentId: string | null = null) => {
        for (const node of nodes) {
          existingMap.set(node.id, { node, parentId })
          if (node.children && node.children.length > 0) {
            flattenTree(node.children, node.id)
          }
        }
      }
      flattenTree(tree.value)

      const deletedIds = new Set(remoteNodes.filter((node) => node.deletedAt).map((node) => node.id))
      const capturedIds = new Set<string>()
      for (const n of remoteNodes.filter((node) => node.deletedAt)) {
        if (capturedIds.has(n.id) || (n.parentId && deletedIds.has(n.parentId))) continue
        const existing = existingMap.get(n.id)
        if (existing && !trash.value.some((entry) => findNode([entry.node], n.id))) {
          const location = findParentList(tree.value, n.id)
          const entry: TrashEntry = {
            node: existing.node,
            originalParentId: existing.parentId,
            originalIndex: location?.index ?? 0,
            deletedAt: Date.parse(n.deletedAt) || Date.now(),
          }
          trash.value.unshift(entry)
          moveTrashEntryFiles(entry, 'trashPage')
          for (const { node } of flattenNodes([entry.node])) capturedIds.add(node.id)
        }
      }

      // A tombstone may be the first state this device has ever seen. Rebuild
      // those trash entries directly from the encrypted cloud metadata.
      for (const entry of trashEntriesFromRemote(remoteNodes)) {
        if (!trash.value.some((current) => findNode([current.node], entry.node.id))) {
          trash.value.unshift(entry)
          moveTrashEntryFiles(entry, 'trashPage')
        }
      }

      for (const n of remoteNodes.filter((node) => !node.deletedAt)) {
        const trashIndex = trash.value.findIndex((entry) => entry.node.id === n.id)
        if (trashIndex !== -1) {
          const [entry] = trash.value.splice(trashIndex, 1)
          moveTrashEntryFiles(entry, 'restorePage')
        }
      }

      // 2. Apply delta changes
      for (const n of remoteNodes) {
        if (n.deletedAt) {
          // Tombstone: remove from existing map
          existingMap.delete(n.id)
          continue
        }

        const subtype: 'desk' | 'folder' | 'page' = n.subtype || (n.type === 'folder' ? 'folder' : 'page')
        const isContainer = subtype === 'desk' || subtype === 'folder'

        if (existingMap.has(n.id)) {
          const entry = existingMap.get(n.id)!
          const existing = entry.node
          // LWW: apply if incoming HLC is >= existing HLC
          if (!existing.hlcTimestamp || (n.hlcTimestamp && n.hlcTimestamp >= existing.hlcTimestamp)) {
            existing.name = n.name || 'Untitled'
            existing.subtype = subtype
            existing.position = n.position !== undefined ? n.position : existing.position
            existing.hlcTimestamp = n.hlcTimestamp || existing.hlcTimestamp
            existing.icon = n.icon !== undefined ? n.icon : existing.icon
            existing.color = n.color !== undefined ? n.color : existing.color
            if (n.metadata !== undefined) Object.assign(existing, metadataNodeFields(n.metadata))
            entry.parentId = n.parentId !== undefined ? n.parentId : entry.parentId
            if ((isContainer || subtype === 'page') && !existing.children) {
              existing.children = []
            }
          }
        } else {
          // New node
          const newNode: CorpusNode = {
            id: n.id,
            name: n.name || 'Untitled',
            subtype,
            position: n.position || 0,
            hlcTimestamp: n.hlcTimestamp || 0,
            icon: n.icon || null,
            color: n.color || null,
            ...metadataNodeFields(n.metadata),
            children: [],
            ...(subtype !== 'desk' ? { expanded: n.expanded ?? true } : {}),
          }
          existingMap.set(n.id, { node: newNode, parentId: n.parentId ?? null })
        }
      }

      // 3. Clear existing children and reassemble hierarchy
      for (const entry of existingMap.values()) {
        if (entry.node.children) {
          entry.node.children = []
        }
      }

      const roots: CorpusNode[] = []
      for (const entry of existingMap.values()) {
        const item = entry.node
        const parentId = entry.parentId
        if (parentId && existingMap.has(parentId)) {
          const parent = existingMap.get(parentId)!.node
          parent.children = parent.children || []
          parent.children.push(item)
          if (parent.subtype === 'page' && parent.expanded === undefined) parent.expanded = true
        } else {
          roots.push(item)
        }
      }

      // 4. Sort by position at every level
      const sortLevel = (list: CorpusNode[]) => {
        list.sort((a, b) => (a.position || 0) - (b.position || 0))
        for (const node of list) {
          if (node.children) sortLevel(node.children)
        }
      }
      sortLevel(roots)

      tree.value = roots

      // 5. Clean up focusStack, activePageId, and any open folder overview
      focusStack.value = focusStack.value.filter((id) => findNode(tree.value, id) !== null)

      if (overviewFolderId.value && !findNode(tree.value, overviewFolderId.value)) {
        overviewFolderId.value = null
      }

      if (!activePageId.value || !findNode(tree.value, activePageId.value)) {
        setActivePageQuietly(syncedLastPageId() ?? findFirstPage(tree.value)?.id ?? null)
      }

      persistManifest()
      return
    }

    // ── Full Tree Reconstruction ────────────────────────────────────────────
    const activeNodes = remoteNodes.filter((n) => !n.deletedAt)
    const remoteTrash = trashEntriesFromRemote(remoteNodes)
    const remoteIds = new Set(remoteNodes.map((node) => node.id))
    const activeIds = new Set(activeNodes.map((node) => node.id))
    for (const entry of trash.value.filter((item) => activeIds.has(item.node.id))) {
      moveTrashEntryFiles(entry, 'restorePage')
    }
    const localOnlyTrash = trash.value.filter((entry) => !remoteIds.has(entry.node.id))
    trash.value = [...remoteTrash, ...localOnlyTrash]
    for (const entry of remoteTrash) moveTrashEntryFiles(entry, 'trashPage')
    const nodeMap = new Map<string, CorpusNode>()
    const roots: CorpusNode[] = []

    // 1. Create CorpusNode items (preserve existing expanded state if known)
    for (const n of activeNodes) {
      const existing = findNode(tree.value, n.id)
      const subtype: 'desk' | 'folder' | 'page' = n.subtype || (n.type === 'folder' ? 'folder' : 'page')
      nodeMap.set(n.id, {
        id: n.id,
        name: n.name || 'Untitled',
        subtype,
        position: n.position || 0,
        hlcTimestamp: n.hlcTimestamp || 0,
        icon: n.icon || null,
        color: n.color || null,
        ...metadataNodeFields(n.metadata),
        children: [],
        ...(subtype !== 'desk' ? { expanded: existing?.expanded ?? n.expanded ?? true } : {}),
      })
    }

    // 2. Build tree hierarchy
    for (const n of activeNodes) {
      const item = nodeMap.get(n.id)
      if (!item) continue
      if (n.parentId && nodeMap.has(n.parentId)) {
        const parent = nodeMap.get(n.parentId)!
        parent.children = parent.children || []
        parent.children.push(item)
        if (parent.subtype === 'page' && parent.expanded === undefined) parent.expanded = true
      } else {
        roots.push(item)
      }
    }

    // 3. Sort by position
    const sortLevel = (list: CorpusNode[]) => {
      list.sort((a, b) => (a.position || 0) - (b.position || 0))
      for (const node of list) {
        if (node.children) sortLevel(node.children)
      }
    }
    sortLevel(roots)

    tree.value = roots

    // Clean up focusStack, and any open folder overview, if their targets no longer exist
    focusStack.value = focusStack.value.filter((id) => findNode(tree.value, id) !== null)

    if (overviewFolderId.value && !findNode(tree.value, overviewFolderId.value)) {
      overviewFolderId.value = null
    }

    if (!activePageId.value || !findNode(tree.value, activePageId.value)) {
      setActivePageQuietly(syncedLastPageId() ?? findFirstPage(tree.value)?.id ?? null)
    }

    persistManifest()
  }

  return {
    tree,
    trash,
    activePageId,
    syncedLastPageId,
    settleStartupPage,
    syncLastOpenedPage,
    selectedNodeId,
    selectedNodeIds,
    isSelectionMode,
    editingNodeId,
    focusStack,
    pinned,
    recentPageIds,
    searchQuery,
    searchView,
    searchTextIndex,
    searchActive,
    parsedSearch,
    searchNeedsBody,
    searchBodyMatcher,
    searchScope,
    searchScopeNode,
    searchWholeCorpus,
    searchRootNodes,
    replaceOpen,
    replaceText,
    searchResults,
    flatSearchResults,
    overviewFolderId,
    overviewView,
    overviewSort,
    overviewShowBalance,
    overviewPreview,
    overviewShowShape,
    notesViewOpen,
    activePageNotes,
    isWritingPage,
    toggleNotesView,
    currentDesk,
    currentDeskName,
    visibleTree,
    filteredTree,
    getCurrentContainerId,
    addNode,
    commitImportedContainers,
    discardImportedContainers,
    markPageSaved,
    resendNode,
    discardUnsavedNode,
    renameNode,
    deleteNode,
    deleteNodes,
    trashNode,
    trashNodes,
    selectNode,
    toggleNodeSelection,
    collapseAll,
    rangeSelectNode,
    selectAllNodes,
    clearSelection,
    restoreTrashEntry,
    deleteTrashEntryForever,
    applyRemotePurges,
    purgeExpiredTrash,
    moveNode,
    getHeaderDropInfo,
    moveNodeToHeaderParent,
    captureNodePlacements,
    restoreNodePlacements,
    toggleFolder,
    toggleNode,
    revealNode,
    setNodeSubtype,
    setNodeIcon,
    setNodeColor,
    setDocumentMetadata,
    setPublishingProfile,
    setPageFrontmatter,
    setPublishedFile,
    setPageSynopsis,
    setPageStatus,
    setNodeDue,
    setPageFontFamily,
    pageSetupFor,
    setPageSetup,
    applyPageSetupToFolder,
    enterDesk,
    exitDesk,
    openFolderOverview,
    closeFolderOverview,
    findNode: (id: string) => findNode(tree.value, id),
    findFirstPage: () => findFirstPage(tree.value),
    persistManifest,
    loadManifest,
    applyRemoteTree,
  }
})
